/**
 * Certificate Verification Page - Public
 *
 * Public page for anyone to verify the authenticity of a Halal certificate.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';
import { Badge } from '../../components/common/Badge';
import { verifyCertificate } from '../../services/mockData/certificates';
import { formatDate } from '../../utils/formatting';
import type { Certificate } from '../../types/certificate';

export const CertificateVerificationPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    isValid: boolean;
    certificate?: Certificate;
    message: string;
  } | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);

    // Simulate API call delay
    setTimeout(() => {
      const result = verifyCertificate(certificateNumber);
      setVerificationResult(result);
      setIsVerifying(false);
    }, 1000);
  };

  const handleReset = () => {
    setCertificateNumber('');
    setVerificationResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-green-800 mb-2">
            {t('certificate.verification.title')}
          </h1>
          <p className="text-gray-600">{t('certificate.verification.subtitle')}</p>
        </div>

        {/* Verification Form */}
        <Card className="mb-6">
          <CardBody>
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('certificate.verification.certificateNumber')}
                </label>
                <Input
                  type="text"
                  name="certificateNumber"
                  value={certificateNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCertificateNumber(e.target.value)
                  }
                  placeholder="Enter certificate number or verification code..."
                  required
                  disabled={isVerifying}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Example: CERT-20250101-001 or VER-2025-001-KW-JAKIM
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  fullWidth
                  disabled={isVerifying || !certificateNumber.trim()}
                >
                  {isVerifying
                    ? t('certificate.verification.verifying')
                    : t('certificate.verification.verify')}
                </Button>
                {verificationResult && (
                  <Button type="button" variant="outline" onClick={handleReset}>
                    {t('common:actions.clear')}
                  </Button>
                )}
              </div>
            </form>
          </CardBody>
        </Card>

        {/* Verification Result */}
        {verificationResult && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Verification Result</h3>
                {verificationResult.isValid ? (
                  <Badge variant="success">{t('certificate.verification.valid')}</Badge>
                ) : (
                  <Badge variant="error">{t('certificate.verification.invalid')}</Badge>
                )}
              </div>
            </CardHeader>
            <CardBody>
              {verificationResult.isValid && verificationResult.certificate ? (
                <div className="space-y-6">
                  {/* Success Message */}
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <div className="flex items-center">
                      <CheckCircle2 className="w-6 h-6 text-green-600 mr-3 shrink-0" />
                      <div>
                        <p className="font-semibold text-green-800">
                          {t('certificate.verification.verificationSuccess')}
                        </p>
                        <p className="text-sm text-green-700">{verificationResult.message}</p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('certificate.verification.certificateNumber')}</p>
                      <p className="font-semibold text-gray-800">
                        {verificationResult.certificate.certificateNumber}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">{t('certificate.verification.status')}</p>
                      <Badge variant="success">{verificationResult.certificate.status}</Badge>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">{t('certificate.verification.companyName')}</p>
                      <p className="font-semibold text-gray-800">
                        {verificationResult.certificate.companyName}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">{t('certificate.verification.issuedBy')}</p>
                      <p className="font-semibold text-gray-800">
                        {verificationResult.certificate.issuedBy}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">{t('certificate.verification.issueDate')}</p>
                      <p className="font-semibold text-gray-800">
                        {formatDate(verificationResult.certificate.issueDate)}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">{t('certificate.verification.expiryDate')}</p>
                      <p className="font-semibold text-gray-800">
                        {formatDate(verificationResult.certificate.expiryDate)}
                      </p>
                    </div>

                    {verificationResult.certificate.productCategory && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-500">{t('certificate.verification.productCategory')}</p>
                        <p className="font-semibold text-gray-800">
                          {verificationResult.certificate.productCategory}
                        </p>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">{t('certificate.verification.scope')}</p>
                      <p className="font-semibold text-gray-800">
                        {verificationResult.certificate.scope}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-semibold text-gray-800">
                        {verificationResult.certificate.address}
                      </p>
                    </div>
                  </div>

                  {/* Verification Code */}
                  <div className="border-t pt-4">
                    <p className="text-xs text-gray-500 mb-1">Verification Code:</p>
                    <p className="font-mono text-sm text-gray-700">
                      {verificationResult.certificate.verificationCode}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-center">
                    <XCircle className="w-6 h-6 text-red-600 mr-3 shrink-0" />
                    <div>
                      <p className="font-semibold text-red-800">
                        {t('certificate.verification.verificationFailed')}
                      </p>
                      <p className="text-sm text-red-700">{verificationResult.message}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-600">
          <p>HALMAS - Halal Management System</p>
          <p className="mt-1">
            <a href="/" className="text-blue-600 hover:underline">
              Back to Home
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
